import { useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  LazyWrapper,
} from "pixelize-design-library";
import { Bell, Car, ChevronRight, Heart, LogOut, MapPin, Package, User } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { selectUser, useSession } from "@core/auth";
import { brandColors } from "@core/theme";
import { navigationMenus } from "@modules/registry";
import { useCartCount } from "@modules/cart";
import { useWishlistIds } from "@modules/catalog/hooks/useWishlistIds";
import { useUnreadCount } from "@modules/notifications/hooks/useUnreadCount";
import { ShopForVehicleSync } from "@modules/shared";
import { AddVehiclePrompt } from "@modules/vehicles/components/AddVehiclePrompt";
import { AMBER, NAVY } from "./accent";
import { AppTopBar } from "./AppTopBar";
import { ShopForVehicleStrip } from "./ShopForVehicleStrip";
import { Footer } from "./Footer";
import { LAYOUT_MAX, LAYOUT_PX } from "./layoutConstants";
import { SupportButton } from "./SupportButton";

type DrawerMenuItemProps = { icon: LucideIcon; label: string; onClick: () => void };

const DrawerMenuItem = ({ icon: Icon, label, onClick }: DrawerMenuItemProps) => (
  <Flex
    as="button"
    type="button"
    align="center"
    gap={3}
    px={2}
    py={3}
    borderRadius="md"
    transition="background 0.15s ease"
    _hover={{ bg: brandColors.gray[50] }}
    onClick={onClick}
    w="100%"
  >
    <Flex
      align="center"
      justify="center"
      boxSize="36px"
      borderRadius="full"
      bg={brandColors.gray[100]}
      color={NAVY[800]}
    >
      <Icon size={17} />
    </Flex>
    <Box flex={1} textAlign="left" fontSize="sm" fontWeight="500" color={brandColors.gray[800]}>
      {label}
    </Box>
    <ChevronRight size={16} color={brandColors.gray[400]} />
  </Flex>
);

/** Site chrome — custom AppTopBar (not pixelize Header). */
export const AppShell = () => {
  const [accountOpen, setAccountOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isCheckoutStep = location.pathname.startsWith("/checkout");
  const user = useSession(selectUser);
  const signOut = useSession((state) => state.signOut);
  const { data: cart } = useCartCount();
  const { data: wishlistIds } = useWishlistIds();
  const { data: unreadCount } = useUnreadCount();

  const go = (url: string) => {
    setAccountOpen(false);
    setMobileNavOpen(false);
    void navigate(url);
  };

  const initial = user ? user.name.charAt(0).toUpperCase() : "A";

  return (
    <Flex direction="column" minH="100vh" bg={brandColors.backgroundColor.main}>
      <Box
        position="sticky"
        top={0}
        zIndex={10}
        sx={{ backdropFilter: "blur(14px) saturate(160%)" }}
      >
        <AppTopBar
          cartCount={cart?.itemCount}
          wishlistCount={wishlistIds?.size}
          notificationCount={unreadCount?.unread}
          userName={user?.name ?? null}
          userInitial={initial}
          onAccountClick={() => setAccountOpen(true)}
          onWishlistClick={() => go("/wishlist")}
          onNotificationsClick={() => go("/notifications")}
          onCartClick={() => go("/cart")}
          onMobileNavOpen={() => setMobileNavOpen(true)}
        />
        <ShopForVehicleStrip />
      </Box>
      <ShopForVehicleSync />
      <Box as="main" flex="1" px={LAYOUT_PX} py={{ base: 4, md: 6, lg: 8 }}>
        <Box maxW={LAYOUT_MAX.content} mx="auto" w="100%">
          <LazyWrapper minHeight="60vh">
            <Outlet />
          </LazyWrapper>
        </Box>
      </Box>
      {isCheckoutStep ? null : <Footer onNavigate={go} />}
      <AddVehiclePrompt />
      <SupportButton />
      <Drawer
        isOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        placement="left"
        size="sm"
      >
        <DrawerHeader>
          <Text fontWeight="700">Menu</Text>
        </DrawerHeader>
        <DrawerBody>
          <Flex direction="column" gap={1}>
            {navigationMenus.map((item) => (
              <DrawerMenuItem
                key={item.id}
                icon={item.icon}
                label={item.title}
                onClick={() => go(item.url)}
              />
            ))}
            <DrawerMenuItem icon={Heart} label="Wishlist" onClick={() => go("/wishlist")} />
            <DrawerMenuItem
              icon={Bell}
              label="Notifications"
              onClick={() => go("/notifications")}
            />
          </Flex>
        </DrawerBody>
      </Drawer>
      <Drawer
        isOpen={accountOpen}
        onClose={() => setAccountOpen(false)}
        placement="right"
        size="sm"
      >
        <DrawerHeader>
          <Flex align="center" gap={3}>
            <Flex
              align="center"
              justify="center"
              boxSize="48px"
              borderRadius="full"
              bgGradient={`linear(135deg, ${NAVY[700]}, ${AMBER[600]})`}
              color="white"
              fontSize="lg"
              fontWeight="700"
              flexShrink={0}
            >
              {initial}
            </Flex>
            <Box minW={0}>
              <Text fontWeight="700" fontSize="md" isTruncated>
                {user ? user.name : "Account"}
              </Text>
              {user === null ? null : (
                <Text fontSize="xs" color={brandColors.gray[500]} isTruncated>
                  {user.email}
                </Text>
              )}
            </Box>
          </Flex>
        </DrawerHeader>
        <DrawerBody>
          <Flex direction="column" gap={1} pt={2}>
            <DrawerMenuItem icon={User} label="Profile" onClick={() => go("/profile")} />
            <DrawerMenuItem icon={MapPin} label="Addresses" onClick={() => go("/addresses")} />
            <DrawerMenuItem icon={Package} label="Orders" onClick={() => go("/orders")} />
            <DrawerMenuItem icon={Car} label="Garage" onClick={() => go("/vehicles")} />
            <DrawerMenuItem icon={Heart} label="Wishlist" onClick={() => go("/wishlist")} />
          </Flex>
        </DrawerBody>
        <DrawerFooter>
          {user === null ? (
            <Button
              label="Login"
              colorScheme="red"
              variant="outline"
              width="100%"
              leftIcon={<LogOut size={16} />}
              onClick={() => go("/login")}
            />
          ) : (
            <Button
              label="Sign out"
              colorScheme="red"
              variant="outline"
              width="100%"
              leftIcon={<LogOut size={16} />}
              onClick={() => {
                void signOut().then(() => {
                  void navigate("/");
                });
              }}
            />
          )}
        </DrawerFooter>
      </Drawer>
    </Flex>
  );
};
